import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  addDistributorAPI,
  deleteDistributorAPI,
  fetchDistributorDetailsAPI,
  fetchDistributorsAPI,
  syncDistributorAPI,
  updateDistributorAPI,
} from '../apis/distributor';

export const fetchDistributors = createAsyncThunk(
  'distributor/fetchDistributors',
  async (data, { rejectWithValue }) => {
    try {
      const res = await fetchDistributorsAPI(data);
      return res;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const createDistributor = createAsyncThunk(
  'distributor/createDistributor',
  async ({ data }, { rejectWithValue }) => {
    try {
      const res = await addDistributorAPI(data);
      return res;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateDistributor = createAsyncThunk(
  'distributor/updateDistributor',
  async ({ data, distributorId, blazeVendorId }, { rejectWithValue }) => {
    try {
      const res = await updateDistributorAPI(data, distributorId, blazeVendorId);
      return res;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteDistributor = createAsyncThunk(
  'distributor/deleteDistributor',
  async (distributorId, { rejectWithValue }) => {
    try {
      const res = await deleteDistributorAPI(distributorId);
      return res;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchSingleDistributor = createAsyncThunk(
  'distributor/fetchSingleDistributor',
  async (arg, { rejectWithValue }) => {
    try {
      const distributorId = typeof arg === 'object' ? arg.distributorId : arg;
      const blazeVendorId = typeof arg === 'object' ? arg.blazeVendorId : undefined;
      const res = await fetchDistributorDetailsAPI(distributorId, blazeVendorId);
      return res;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const syncDistributor = createAsyncThunk(
  'distributor/syncDistributor',
  async (_, { rejectWithValue }) => {
    try {
      const res = await syncDistributorAPI();
      return res;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const initialState = {
  isLoading: false,
  error: null,
  isDeleting: false,
  distributorsList: {},
  total: 0,
  isFetching: false,
  distributorDetails: {},
  syncDistributorLoader: false,
};

const distributorSlice = createSlice({
  name: 'distributor',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDistributors.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDistributors.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload ?? {};
        state.distributorsList = payload?.data ?? [];
        state.total = payload?.total ?? 0;
        state.distributorDetails = {};
      })
      .addCase(fetchDistributors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    builder
      .addCase(createDistributor.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDistributor.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createDistributor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    builder
      .addCase(updateDistributor.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateDistributor.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateDistributor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    builder
      .addCase(deleteDistributor.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteDistributor.fulfilled, (state) => {
        state.isDeleting = false;
      })
      .addCase(deleteDistributor.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchSingleDistributor.pending, (state) => {
        state.isFetching = true;
        state.error = null;
        state.distributorDetails = {};
      })
      .addCase(fetchSingleDistributor.fulfilled, (state, action) => {
        state.isFetching = false;
        const payload = action.payload ?? {};
        state.distributorDetails =
          payload.distributorDetails ??
          payload.distributor ??
          payload.data ??
          payload;
      })
      .addCase(fetchSingleDistributor.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      });
    builder
      .addCase(syncDistributor.pending, (state) => {
        state.syncDistributorLoader = true;
      })
      .addCase(syncDistributor.fulfilled, (state) => {
        state.syncDistributorLoader = false;
      })
      .addCase(syncDistributor.rejected, (state, action) => {
        state.syncDistributorLoader = false;
        state.error = action.payload;
      });
  },
});

export default distributorSlice.reducer;
